import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Select } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useDocumentTitle } from "@mantine/hooks";
import { IconCalendar } from "@tabler/icons-react";
import { useShallow } from "zustand/react/shallow";

import { WithPatientNavbar } from "@layouts/WithPatientNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { CustomPhoneInput } from "@components/CustomPhoneInput";

import { calculateBMI } from "@utils/calculateBMI";
import { formatDateToString, parseDate } from "@utils/dateValidation";
import { handleOnChange } from "@utils/handleOnChange";
import { prefixPhoneNumberWithPlus } from "@utils/phoneNumberPrefix";

import {
  bloodTypeValues,
  getDietValues,
  getGenderValues,
  getSmokingHabitsValues,
  getYesNoValues,
  insuranceTypeValues,
} from "@data/dropdownValues";

import { PAGE_TITLE } from "@constants/index";

import { useUpdatePatient } from "@hooks/mutations/patient/useUpdatePatient";
import { useGetPatientAllergies } from "@hooks/queries/patient/useGetPatientAllergies";
import { useGetPatientByPhoneNumber } from "@hooks/queries/patient/useGetPatientByPhoneNumber";
import { useGetPatientMedicalHistory } from "@hooks/queries/patient/useGetPatientMedicalHistory";
import { useGetPatientSurgeries } from "@hooks/queries/patient/useGetPatientSurgeries";
import { useGetSinglePatient } from "@hooks/queries/patient/useGetSinglePatient";
import { useAppStore } from "@hooks/store/useAppStore";

import { useAuth } from "@contexts/auth";

import {
  Patient,
  PatientAllergy,
  PatientMedicalHistoryItem,
  PatientSurgicalHistoryItem,
} from "@customTypes/patient";

import { AllergyCard } from "./components/AllergyCard";
import { CardHeader } from "./components/CardHeader";
import { MedicalHistoryCard } from "./components/MedicalHistoryCard";
import { SurgicalHistoryCard } from "./components/SurgicalHistoryCard";
import { StyledProfile } from "./Profile.styled";


const getPlaceholder = (isEditActive: boolean) => {
  return isEditActive ? "" : "N/A";
};

export const Profile = () => {
  const { t } = useTranslation();
  useDocumentTitle(`${t("patient-profile")} • ${PAGE_TITLE}`);

  const [users] = useAppStore(useShallow(state => [state.users]));
  const { id: userId } = useAuth();

  const [isCard1Editable, setIsCard1Editable] = useState(false);
  const [isCard2Editable, setIsCard2Editable] = useState(false);
  const [isCard3Editable, setIsCard3Editable] = useState(false);
  const [isCard4Editable, setIsCard4Editable] = useState(false);
  const [isCard5Editable, setIsCard5Editable] = useState(false);
  const [isCard6Editable, setIsCard6Editable] = useState(false);
  const [dob, setDob] = useState<Date | null>(null);
  const [gender, setGender] = useState<string | null>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [altPhoneNumber, setAltPhoneNumber] = useState("");
  const [ECphoneNumber, setECPhoneNumber] = useState(""); // EC = Emergency Contact
  const [isDonor, setIsDonor] = useState<string | null>(null);
  const [bloodType, setBloodType] = useState<string | null>(null);
  const [diet, setDiet] = useState<string | null>(null);
  const [smokingHabits, setSmokingHabits] = useState<string | null>(null);
  const [insuranceType, setInsuranceType] = useState<string | null>(null);
  const [insuranceExpiryDate, setInsuranceExpiryDate] = useState<Date | null>(
    null
  );
  const [formData, setFormData] = useState({} as Patient);
  const [initialFormData, setInitialFormData] = useState(formData);
  const [allergies, setAllergies] = useState<PatientAllergy[] | []>([]);
  const [surgeries, setSurgeries] = useState<PatientSurgicalHistoryItem[] | []>(
    []
  );
  const [medicalHistory, setMedicalHistory] = useState<
    PatientMedicalHistoryItem[] | []
  >([]);
  const { patientId } = useParams();

  const { data: profileData, isLoading: isGetProfileLoading } =
    useGetSinglePatient({
      patientId: patientId as string,
      reason: users[userId]?.patients[patientId as string]?.reason,
    });
  const { data: allergiesData } = useGetPatientAllergies({
    patientId: patientId as string,
  });
  const { data: surgeriesData } = useGetPatientSurgeries({
    patientId: patientId as string,
  });
  const { data: medicalHistoryData } = useGetPatientMedicalHistory({
    patientId: patientId as string,
  });
  const { mutate: updatePatient, isLoading: isUpdatePatientLoading } =
    useUpdatePatient();

  const { isSuccess: isExistingPhoneNumber } = useGetPatientByPhoneNumber({
    phoneNumber,
    isEnabled: phoneNumber.length >= 10,
  });

  useEffect(() => {
    if (gender) {
      setFormData(prevData => ({
        ...prevData,
        gender,
      }));
    }
  }, [gender]);

  //* Maintain this useEffect because it is used in CardHeader component to update the patient
  useEffect(() => {
    if (dob) {
      setFormData(prevData => ({
        ...prevData,
        dob: formatDateToString(dob),
      }));
    }
  }, [dob]);

  useEffect(() => {
    if (phoneNumber) {
      setFormData(prevData => ({
        ...prevData,
        phoneNumber,
      }));
    }
  }, [phoneNumber]);

  useEffect(() => {
    if (altPhoneNumber) {
      setFormData(prevData => ({
        ...prevData,
        altPhoneNumber,
      }));
    }
  }, [altPhoneNumber]);

  useEffect(() => {
    if (ECphoneNumber) {
      setFormData(prevData => ({
        ...prevData,
        emergencyContactPhoneNumber: ECphoneNumber,
      }));
    }
  }, [ECphoneNumber]);

  useEffect(() => {
    if (isDonor) {
      setFormData(prevData => ({
        ...prevData,
        bloodDonor: isDonor,
      }));
    }
  }, [isDonor]);

  useEffect(() => {
    if (bloodType) {
      setFormData(prevData => ({
        ...prevData,
        bloodType,
      }));
    }
  }, [bloodType]);

  useEffect(() => {
    if (diet) {
      setFormData(prevData => ({
        ...prevData,
        diet,
      }));
    }
  }, [diet]);

  useEffect(() => {
    if (smokingHabits) {
      setFormData(prevData => ({
        ...prevData,
        smokingHabits,
      }));
    }
  }, [smokingHabits]);

  useEffect(() => {
    if (insuranceType) {
      setFormData(prevData => ({
        ...prevData,
        insuranceType,
      }));
    }
  }, [insuranceType]);

  //* Maintain this, refer to dob above
  useEffect(() => {
    if (insuranceExpiryDate) {
      setFormData(prevData => ({
        ...prevData,
        insuranceExpiry: formatDateToString(insuranceExpiryDate),
      }));
    }
  }, [insuranceExpiryDate]);

  useEffect(() => {
    if (profileData) {
      console.log(profileData);
      setFormData(profileData);
      setInitialFormData(profileData);
      setDob(profileData.dob ? parseDate(profileData.dob as string) : null);
      setInsuranceExpiryDate(
        profileData.insuranceExpiry
          ? parseDate(profileData.insuranceExpiry as string)
          : null
      );
    }
  }, [profileData]);

  useEffect(() => {
    console.log("[Patient profile form data]", formData);
  }, [formData]);

  useEffect(() => {
    if (allergiesData) {
      // console.log(allergiesData);
      setAllergies(allergiesData);
    } else setAllergies([]);
    if (surgeriesData) {
      // console.log(surgeriesData);
      setSurgeries(surgeriesData);
    } else setSurgeries([]);
    if (medicalHistoryData) {
      // console.log(medicalHistoryData);
      setMedicalHistory(medicalHistoryData);
    } else setMedicalHistory([]);
  }, [allergiesData, surgeriesData, medicalHistoryData]);

  useEffect(() => {
    // todo: Why this? Investigate this later - But normally it's supposed to cause a re-render for something else on the page ot happen
    return;
  }, [isUpdatePatientLoading]);

  return (
    <WithSidebar hasBackground={false} hasPatientNavbar>
      <WithPatientNavbar>
        <StyledProfile>
          <div className="card card-1">
            <CardHeader
              title={t("personal-info")}
              isUpdatePatientLoading={isUpdatePatientLoading}
              isCardEditable={isCard1Editable}
              setIsCardEditable={setIsCard1Editable}
              isGetProfileLoading={isGetProfileLoading}
              formData={formData}
              setFormData={setFormData}
              setInitialFormData={setInitialFormData}
              initialFormData={initialFormData}
              updatePatient={updatePatient}
              initialFormDataStatesResets={[() => setGender(null)]}
            />
            <div className="body">
              <div className="form-field">
                <label htmlFor="firstName">{t("first-name")}</label>
                <input
                  className="input"
                  type="text"
                  id="firstName"
                  readOnly={!isCard1Editable}
                  name="firstName"
                  placeholder={getPlaceholder(isCard1Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.firstName ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="middleName">{t("middle-name")}</label>
                <input
                  className="input"
                  type="text"
                  id="middleName"
                  readOnly={!isCard1Editable}
                  name="middleName"
                  placeholder={getPlaceholder(isCard1Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.middleName ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="surname">{t("surname")}</label>
                <input
                  className="input"
                  type="text"
                  id="surname"
                  readOnly={!isCard1Editable}
                  name="surname"
                  placeholder={getPlaceholder(isCard1Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.surname ?? ""}
                />
              </div>
              <div className="form-field">
                <DatePickerInput
                  label={t("dob")}
                  placeholder={t("select-date")}
                  value={dob}
                  onChange={setDob}
                  disabled={!isCard1Editable}
                  leftSection={<IconCalendar size={18} stroke={1.5} />}
                />
              </div>
              <div className="form-field">
                <Select
                  label={t("gender")}
                  placeholder={t("select-gender")}
                  data={getGenderValues()}
                  value={formData?.gender}
                  onChange={setGender}
                  disabled={!isCard1Editable}
                />
              </div>
              <div className="form-field">
                <label htmlFor="email">{t("email")}</label>
                <input
                  className="input"
                  type="email"
                  id="email"
                  readOnly={!isCard1Editable}
                  name="email"
                  placeholder={getPlaceholder(isCard1Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.email ?? ""}
                />
              </div>
            </div>
          </div>

          <div className="card card-2">
            <CardHeader
              title={t("more-personal-info")}
              isUpdatePatientLoading={isUpdatePatientLoading}
              isCardEditable={isCard2Editable}
              setIsCardEditable={setIsCard2Editable}
              isGetProfileLoading={isGetProfileLoading}
              formData={formData}
              setFormData={setFormData}
              setInitialFormData={setInitialFormData}
              initialFormData={initialFormData}
              updatePatient={updatePatient}
              hasErrors={
                isExistingPhoneNumber &&
                prefixPhoneNumberWithPlus(phoneNumber) !==
                  prefixPhoneNumberWithPlus(initialFormData.phoneNumber)
              }
            />
            <div className="body">
              <div className="form-field">
                <label htmlFor="phone">{t("phone-number")}</label>
                <CustomPhoneInput
                  phone={formData.phoneNumber || ""}
                  setPhone={setPhoneNumber}
                  readOnly={!isCard2Editable}
                  placeholder={getPlaceholder(isCard2Editable)}
                  isExistingNumber={
                    isExistingPhoneNumber &&
                    prefixPhoneNumberWithPlus(phoneNumber) !==
                      prefixPhoneNumberWithPlus(initialFormData.phoneNumber)
                  }
                />
              </div>
              <div className="form-field">
                <label htmlFor="altPhoneNumber">{t("alt-phone-number")}</label>
                <CustomPhoneInput
                  phone={formData.altPhoneNumber || ""}
                  setPhone={setAltPhoneNumber}
                  readOnly={!isCard2Editable}
                  placeholder={getPlaceholder(isCard2Editable)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="country">{t("country")}</label>
                <input
                  className="input"
                  type="text"
                  id="country"
                  readOnly={!isCard2Editable}
                  name="country"
                  placeholder={getPlaceholder(isCard2Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.country ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="state">{t("state")}</label>
                <input
                  className="input"
                  type="text"
                  id="state"
                  readOnly={!isCard2Editable}
                  name="state"
                  placeholder={getPlaceholder(isCard2Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.state ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="city">{t("city")}</label>
                <input
                  className="input"
                  type="text"
                  id="city"
                  readOnly={!isCard2Editable}
                  name="city"
                  placeholder={getPlaceholder(isCard2Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.city ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="zone">{t("zone")}</label>
                <input
                  className="input"
                  type="text"
                  id="zone"
                  readOnly={!isCard2Editable}
                  name="zone"
                  placeholder={getPlaceholder(isCard2Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.zone ?? ""}
                />
              </div>
            </div>
          </div>

          <div className="card card-3">
            <CardHeader
              title={t("emergency-contact")}
              isUpdatePatientLoading={isUpdatePatientLoading}
              isCardEditable={isCard3Editable}
              setIsCardEditable={setIsCard3Editable}
              isGetProfileLoading={isGetProfileLoading}
              formData={formData}
              setFormData={setFormData}
              setInitialFormData={setInitialFormData}
              initialFormData={initialFormData}
              updatePatient={updatePatient}
            />
            <div className="body">
              <div className="form-field">
                <label htmlFor="emergencyContactName">{t("name")}</label>
                <input
                  className="input"
                  type="text"
                  placeholder={getPlaceholder(isCard3Editable)}
                  id="emergencyContactName"
                  readOnly={!isCard3Editable}
                  name="emergencyContactName"
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.emergencyContactName ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="emergencyContactPhoneNumber">
                  {t("phone-number")}
                </label>
                <CustomPhoneInput
                  phone={formData?.emergencyContactPhoneNumber ?? ""}
                  setPhone={setECPhoneNumber}
                  readOnly={!isCard3Editable}
                  placeholder={getPlaceholder(isCard3Editable)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="emergencyContactRelationship">
                  {t("relationship")}
                </label>
                <input
                  className="input"
                  type="text"
                  placeholder={getPlaceholder(isCard3Editable)}
                  id="emergencyContactRelationship"
                  readOnly={!isCard3Editable}
                  name="emergencyContactRelationship"
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.emergencyContactRelationship ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="emergencyContactCountry">{t("country")}</label>
                <input
                  className="input"
                  type="text"
                  id="emergencyContactCountry"
                  readOnly={!isCard3Editable}
                  name="emergencyContactCountry"
                  placeholder={getPlaceholder(isCard3Editable)}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.emergencyContactCountry ?? ""}
                />
              </div>
            </div>
          </div>

          <AllergyCard allergies={allergies} />
          <MedicalHistoryCard medicalHistory={medicalHistory} />
          <SurgicalHistoryCard surgeries={surgeries} />

          <div className="card card-4">
            <CardHeader
              title={t("medical-info")}
              isUpdatePatientLoading={isUpdatePatientLoading}
              isCardEditable={isCard4Editable}
              setIsCardEditable={setIsCard4Editable}
              isGetProfileLoading={isGetProfileLoading}
              formData={formData}
              setFormData={setFormData}
              setInitialFormData={setInitialFormData}
              initialFormData={initialFormData}
              updatePatient={updatePatient}
              initialFormDataStatesResets={[
                () => setBloodType(null),
                () => setIsDonor(null),
              ]}
            />
            <div className="body">
              <div className="form-field">
                <Select
                  label={t("blood-type")}
                  placeholder={t("select-blood-type")}
                  data={bloodTypeValues}
                  disabled={!isCard4Editable}
                  value={formData?.bloodType}
                  onChange={setBloodType}
                />
              </div>
              <div className="form-field">
                <Select
                  label={t("is-patient-donor")}
                  data={getYesNoValues()}
                  placeholder={t("select-option")}
                  value={formData?.bloodDonor}
                  onChange={setIsDonor}
                  disabled={!isCard4Editable}
                />
              </div>
              <div className="form-field">
                <label htmlFor="height">{t("height")}</label>
                <input
                  className="input"
                  type="number"
                  id="height"
                  placeholder={getPlaceholder(isCard4Editable)}
                  readOnly={!isCard4Editable}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.height ?? ""}
                  name="height"
                />
              </div>
              <div className="form-field">
                <label htmlFor="weight">{t("weight")}</label>
                <input
                  className="input"
                  type="number"
                  id="weight"
                  placeholder={getPlaceholder(isCard4Editable)}
                  readOnly={!isCard4Editable}
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.weight ?? ""}
                  name="weight"
                />
              </div>
              <div className="form-field">
                <label htmlFor="bmi">{t("bmi")}</label>
                <input
                  className="input"
                  type="text"
                  id="bmi"
                  value={calculateBMI(
                    formData?.height as string,
                    formData?.weight as string
                  )}
                  readOnly
                />
              </div>
            </div>
          </div>

          <div className="card card-5">
            <CardHeader
              title={t("lifestyle-info")}
              isUpdatePatientLoading={isUpdatePatientLoading}
              isCardEditable={isCard5Editable}
              setIsCardEditable={setIsCard5Editable}
              isGetProfileLoading={isGetProfileLoading}
              formData={formData}
              setFormData={setFormData}
              setInitialFormData={setInitialFormData}
              initialFormData={initialFormData}
              updatePatient={updatePatient}
              initialFormDataStatesResets={[
                () => setDiet(null),
                () => setSmokingHabits(null),
              ]}
            />
            <div className="body">
              <div className="form-field">
                <label htmlFor="occupation">{t("occupation")}</label>
                <input
                  className="input"
                  type="text"
                  id="occupation"
                  placeholder={getPlaceholder(isCard5Editable)}
                  readOnly={!isCard5Editable}
                  name="occupation"
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.occupation ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="alcohol">{t("alcohol-consumption")}</label>
                <input
                  className="input"
                  type="text"
                  id="alcohol"
                  placeholder={getPlaceholder(isCard5Editable)}
                  readOnly={!isCard5Editable}
                  name="drinkingHabits"
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.drinkingHabits ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="exerciseFrequency">
                  {t("exercise-frequency")}
                </label>
                <input
                  className="input"
                  type="text"
                  id="exerciseFrequency"
                  placeholder={getPlaceholder(isCard5Editable)}
                  readOnly={!isCard5Editable}
                  name="exerciseFrequency"
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.exerciseFrequency ?? ""}
                />
              </div>
              <div className="form-field">
                <Select
                  label={t("diet")}
                  placeholder={t("select-option")}
                  disabled={!isCard5Editable}
                  data={getDietValues()}
                  value={formData?.diet}
                  onChange={setDiet}
                />
              </div>
              <div className="form-field">
                <Select
                  label={t("smoking-habits")}
                  placeholder={t("select-option")}
                  disabled={!isCard5Editable}
                  data={getSmokingHabitsValues()}
                  value={formData?.smokingHabits}
                  onChange={setSmokingHabits}
                />
              </div>
            </div>
          </div>

          <div className="card card-6">
            <CardHeader
              title={t("health-coverage")}
              isUpdatePatientLoading={isUpdatePatientLoading}
              isCardEditable={isCard6Editable}
              setIsCardEditable={setIsCard6Editable}
              isGetProfileLoading={isGetProfileLoading}
              formData={formData}
              setFormData={setFormData}
              setInitialFormData={setInitialFormData}
              initialFormData={initialFormData}
              updatePatient={updatePatient}
              initialFormDataStatesResets={[() => setInsuranceType(null)]}
            />
            <div className="body">
              <div className="form-field">
                <Select
                  label={t("payer")}
                  data={insuranceTypeValues}
                  placeholder={t("select-option")}
                  value={formData?.insuranceType}
                  onChange={setInsuranceType}
                  disabled={!isCard6Editable}
                />
              </div>
              <div className="form-field">
                <label htmlFor="insurancePolicyNumber">
                  {t("policy-number")}
                </label>
                <input
                  className="input"
                  type="text"
                  id="insurancePolicyNumber"
                  placeholder={getPlaceholder(isCard6Editable)}
                  readOnly={!isCard6Editable}
                  name="insurancePolicyNumber"
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.insurancePolicyNumber ?? ""}
                />
              </div>
              <div className="form-field">
                <label htmlFor="insuranceProvider">{t("provider")}</label>
                <input
                  className="input"
                  type="text"
                  id="insuranceProvider"
                  placeholder={getPlaceholder(isCard6Editable)}
                  readOnly={!isCard6Editable}
                  name="insuranceProvider"
                  onChange={e => handleOnChange(e, setFormData, formData)}
                  value={formData?.insuranceProvider ?? ""}
                />
              </div>
              <div className="form-field">
                <DatePickerInput
                  label={t("expiry-date")}
                  placeholder={t("select-date")}
                  value={insuranceExpiryDate}
                  onChange={setInsuranceExpiryDate}
                  disabled={!isCard6Editable}
                  leftSection={<IconCalendar size={18} stroke={1.5} />}
                />
              </div>
            </div>
          </div>
        </StyledProfile>
      </WithPatientNavbar>
    </WithSidebar>
  );
};
